import { Avatar, Box, Skeleton, Text } from '@factorialco/deprecated-design-system'
import {
  AccessForSelectFragment,
  AccessSelectorDocument,
  AccessSelectorQuery,
} from 'generated/resources/graphql'

import { EntitySelector } from 'modules/core/components/EntitySelector'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import type { AccessSelectorMultipleProps, AccessSelectorSingleProps } from './types'

const isAccessSelectorSingleProps = (
  props: AccessSelectorSingleProps | AccessSelectorMultipleProps
): props is AccessSelectorSingleProps => {
  return !props.multiple
}

const AccessItem = ({
  access,
  withAvatar,
}: {
  access: AccessForSelectFragment
  withAvatar: boolean
}) => {
  return (
    <Box gap="s8" flexDirection="row" alignItems="center" key={access.id}>
      {withAvatar && <Avatar label={accessName(access)} url={buildUrl(access.avatar)} size="s32" />}
      <Text.Normal size="300">{accessName(access)}</Text.Normal>
    </Box>
  )
}

const AccessSkeleton = ({ withAvatar }: { withAvatar: boolean }) => {
  return (
    <Box gap="s8" flexDirection="row" alignItems="center">
      {withAvatar && <Skeleton width="s32" height="s32" />}
      <Skeleton width="s240" height="s32" />
    </Box>
  )
}

const accessName = (access: AccessForSelectFragment) => {
  return `${access.firstName} ${access.lastName}`
}

export const AccessSelector = (props: AccessSelectorSingleProps | AccessSelectorMultipleProps) => {
  const {
    error,
    mandatory,
    label,
    description,
    external,
    terminated,
    width,
    minWidth,
    emptyLabel,
    withRegularAccess = false,
    alwaysOpen = false,
    withAvatar = true,
    disabled = false,
    excludeCurrentAccess = false,
    excludeIds = [],
  } = props

  const { access: currentAccess } = useCurrentSession()

  if (excludeCurrentAccess && currentAccess) {
    excludeIds.push(currentAccess.id)
  }

  const renderEntity = (entity: AccessForSelectFragment) => (
    <AccessItem withAvatar={withAvatar} access={entity} />
  )

  const renderSkeleton = () => <AccessSkeleton withAvatar={withAvatar} />

  const variables = {
    external,
    terminated,
    excludeIds,
    withRegularAccess,
  }

  const extractConnection = (data: AccessSelectorQuery) => {
    return data.apiCore.accessesConnection
  }

  if (!isAccessSelectorSingleProps(props)) {
    return (
      <EntitySelector.Multiple
        label={label}
        description={description}
        disabled={disabled}
        error={error}
        mandatory={mandatory}
        alwaysOpen={alwaysOpen}
        document={AccessSelectorDocument}
        variables={variables}
        width={width}
        minWidth={minWidth}
        extractConnection={extractConnection}
        renderEntity={renderEntity}
        hideBorder={props.hideBorder}
        value={props.value ?? []}
        onChange={props.onSelect}
        renderSkeleton={renderSkeleton}
        renderValue={(entities) => (
          <Text.Normal ellipsis>
            {entities.length > 0
              ? entities.map((el) => accessName(el)).join(', ')
              : emptyLabel ?? i18n.tp('employees.select_employee', { count: 0 })}
          </Text.Normal>
        )}
      />
    )
  }

  return (
    <EntitySelector
      label={label}
      description={description}
      disabled={disabled}
      error={error}
      mandatory={mandatory}
      document={AccessSelectorDocument}
      variables={variables}
      width={width}
      minWidth={minWidth}
      extractConnection={(data) => data.apiCore.accessesConnection}
      renderEntity={renderEntity}
      value={props.value ?? null}
      onChange={props.onSelect}
      renderSkeleton={renderSkeleton}
      renderValue={(entity) => (
        <Text.Normal>
          {entity !== null
            ? accessName(entity)
            : emptyLabel ?? i18n.tp('employees.select_employee', { count: 1 })}
        </Text.Normal>
      )}
    />
  )
}
